import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  TemplateRef
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '@eview/core';
import { AuthHelpers } from '@eview/core/auth';
import { AllowedPriviledge } from '@eview/core/auth/auth';
import { FEATURE_POST_COMMENTS } from '@eview/core/auth/form-grants';
import { Permission } from '@eview/core/auth/permission';
import {
  FormAttribute,
  FormAttributes,
  FormAttributeType
} from '@eview/core/domain/form/form-attribute';
import {
  VULNERABILITY_DRIVER_KEY,
  VULNERABILITY_ROWS_KEY,
  VulnerabilityRow,
  formatVulnerabilityRowsDisplay,
  isRepeatableRowsAttribute,
  parseVulnerabilityRows,
  resolveRowsCatalog
} from '@eview/core/domain/form/vulnerability-rows';
import { MediaService } from '@eview/core/domain/media/media.service';
import { getFileNameFromMediaUrl } from '@eview/core/domain/media/media-url.helpers';
import { assertReadableUploadFile } from '@eview/core/domain/media/readable-file.validation';
import {
  Post,
  PostManagementLevel,
  PostPriority,
  PostStatus,
  PostType,
  PostMediaType
} from '@eview/core/domain/post/post';
import { PostCommentType } from '@eview/core/domain/post/post-comment';
import { PostHelpers } from '@eview/core/domain/post/post.helpers';
import { PostService } from '@eview/core/domain/post/post.service';
import { Tags } from '@eview/core/domain/tags/tag';
import { selectTags } from '@eview/core/store/selectors/tags.selector';
import { CatalogFacade } from '@eview/core/store/facades/catalog.facade';
import { AppState } from '@eview/core/store/states/app.state';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import { FileSaverService } from '@eview/core';
import { combineLatest, Subscription, Observable, of, BehaviorSubject } from 'rxjs';
import { map, switchMap, distinctUntilChanged, startWith, take } from 'rxjs/operators';
import { ECustomActions, ShowPostDetail } from '../../custom.store';
import { selectUsers } from '@eview/core/store/selectors/users.selector';
import { selectUser } from '@eview/core/store/selectors/user.selector';
import { selectUserPermissions } from '@eview/core/store/selectors/user-permissions.selector';
import { User } from '@eview/core/models/user';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { PostEditorPageComponent } from '../../post-editor/post-editor/post-editor-page.component';
import { MapComponent } from '@eview/features/ui/map/map.component';
import { FormService } from '@eview/core/domain/form/form.service';
import { selectComments } from '@eview/core/store/selectors/comments.selector';
import { selectLinkedPosts } from '@eview/core/store/selectors/linked-posts.selector';
import {
  CreateComments,
  DeleteComment,
  ECommentsActions,
  GetCommentsData,
  GetCommentsSuccess
} from '@eview/core/store/actions/comment.actions';
import {
  blockIncidentLongTextKeypress,
  blockIncidentLongTextPaste,
  hasInvalidIncidentLongText
} from '../../validators/incident-long-text.validation';
interface FormAttributeEx extends FormAttribute {
  value: any;
  /** Pre-parsed vulnerability rows for detail view. */
  vulnerabilityRows?: VulnerabilityRow[];
  vulnerabilityDisplay?: string;
}

interface PostEx extends Post {
  values: FormAttributeEx[];
}

interface Audit {
  created: string
  entity: string
  id: number
  type: string
  user_id: number
  value: string
}

@Component({
  selector: 'eview-post-detail',
  templateUrl: 'post-detail.component.html',
  styleUrls: ['post-detail.component.scss']
})
export class PostDetailComponent implements OnInit, OnDestroy {
  @Input() type: string;
  @Input() formId: string;
  @ViewChild(MapComponent, { static: false }) childRef: MapComponent;
  @ViewChild('unpublishModal', { static: false }) unpublishModal: TemplateRef<any>;
  @ViewChild('removeAnalysisFileModal', { static: false })
  removeAnalysisFileModal: TemplateRef<any>;
  @ViewChild('analysisFilePreviewModal', { static: false })
  analysisFilePreviewModal: TemplateRef<any>;
  @Input() public hideLocationMap: boolean;
  @Output() public onShowLinkedPostClick: EventEmitter<number> = new EventEmitter();
  @Output() public onLinkedPostClick: EventEmitter<Object> = new EventEmitter();
  @Output() public onClose: EventEmitter<any> = new EventEmitter();
  @Output() public onPostUpdate: EventEmitter<any> = new EventEmitter();
  @Output() public onClickViewDetail: EventEmitter<any> = new EventEmitter();

  linkedPosts$: Observable<any>;
  comments$: Observable<any>;
  user$: Observable<any>;
  users$: Observable<User[]>;
  viewOnlyMode$: Observable<any>;
  editComments$: Observable<any>;
  viewPost$: Observable<any>;
  permissionToEdit$: Observable<any>;
  permissionToPublish$: Observable<boolean>;
  canPublishAny$: Observable<boolean>;
  canPublishOwn$: Observable<boolean>;
  permissionToComment$: Observable<boolean>;
  canModerateComments$: Observable<boolean>;

  postActivities$: Observable<Audit>;

  role$: Observable<any>;
  showHistory$: Observable<any>;
  showEdit$: Observable<any>;
  showDelete$: Observable<any>;
  isReportStatusEnabled$: Observable<any>;
  isResponseLevelAllowed$: Observable<any>;
  isReportPriorityEnabled$: Observable<any>;
  modifyPersonalReport$: Observable<any>;

  /** Active post form_id for form-grant checks. Never use "any form" for edits. */
  private formIdSubject = new BehaviorSubject<number | null>(null);
  private postFormId$: Observable<number | null>;



  AllowedPriviledge = AllowedPriviledge;
  FormAttributeType = FormAttributeType;
  AuthHelpers = AuthHelpers;
  Permission = Permission;
  PostHelpers = PostHelpers;
  PostPriority = PostPriority;
  /** Report comments UI — see FEATURE_POST_COMMENTS in form-grants.ts */
  FEATURE_POST_COMMENTS = FEATURE_POST_COMMENTS;
  format: string = environment.format.date;
  isEditing: boolean = false;
  showComments: boolean = false;
  PostMediaType = PostMediaType;
  private subs: Subscription;
  postManagementLevels = [
    PostManagementLevel.Unknown,
    PostManagementLevel.Subregional,
    PostManagementLevel.Regional,
    PostManagementLevel.National
  ];
  PostCommentType = PostCommentType;
  mediaUrls: any = {};
  PostType = PostType;

  _post: Post = null;
  post: PostEx = null;
  analysisDraft = '';
  analysisDraftTouched = false;
  analysisEditing = false;
  analysisComposerActive = false;
  analysisMediaIdDraft: number | null = null;
  analysisMediaPreview: { url: string; caption: string } | null = null;
  analysisMediaUploading = false;
  analysisMediaError = '';
  pendingAnalysisFile: File | null = null;
  analysisPdfPreviewSafe: SafeResourceUrl | null = null;
  analysisPreviewMode: 'confirm' | 'view' = 'view';
  private analysisPdfPreviewBlobUrl: string | null = null;
  private analysisFilePreviewModalRef: NgbModalRef | null = null;
  private readonly analysisFilePreviewModalOptions: NgbModalOptions = {
    centered: true,
    backdrop: 'static',
    scrollable: true,
    size: 'lg',
    windowClass: 'media-upload-confirm-modal'
  };
  unpublishReason = '';
  unpublishReasonErrorKey: string | null = null;
  readonly unpublishReasonMaxLength = 2000;
  showDeleteConfirmation: boolean;
  constructor(
    public store: Store<AppState>,
    private actions$: Actions,
    private mediaService: MediaService,
    private postService: PostService,
    private sanitizer: DomSanitizer,
    private fileSaverService: FileSaverService,
    private modalService: NgbModal,
    private formService: FormService,
    private catalog: CatalogFacade,
  ) {
    this.subs = new Subscription();
    this.linkedPosts$ = this.store.select(selectLinkedPosts).pipe(
      map(data => (data ? (data.results.length ? data.results : null) : null))
    );
    this.comments$ = this.store.select(selectComments);
    this.user$ = this.store.select(selectUser);
    this.users$ = this.store.select(selectUsers).pipe(map(users => (users ? users.results : [])));
    this.viewOnlyMode$ = this.user$.pipe(map(users => (users ? true : false)));
    // Form-scoped grants for the open post — always keyed by a concrete form id.
    const showPayload$ = this.actions$.pipe(
      ofType<ShowPostDetail>(ECustomActions.ShowPostDetail),
      map(a => a.payload as Post | null),
      startWith(null as Post | null)
    );
    this.subs.add(
      showPayload$.subscribe(post => {
        const id =
          this.resolvePostFormId(post) ??
          this.resolvePostFormId(this.post) ??
          this.inputFormId();
        this.formIdSubject.next(id);
      })
    );
    this.postFormId$ = this.formIdSubject.asObservable().pipe(distinctUntilChanged());

    const formCan = (action: import('@eview/core/auth/form-grants').FormGrantAction) =>
      this.postFormId$.pipe(
        switchMap(formId => {
          // Without form id, deny scoped mutations (avoids form-2 grants applying to form 1).
          // Super roles still pass via canForm (isAdmin) when form id is not resolved yet.
          if (
            formId == null &&
            action !== 'view' &&
            action !== 'create' &&
            action !== 'analysis' &&
            action !== 'export'
          ) {
            return this.store.select(selectUserPermissions).pipe(
              map(up => !!(up && up.isAdmin))
            );
          }
          return AuthHelpers.User.CanForm(this.store, formId, action);
        })
      );

    this.viewPost$ = combineLatest([
      this.user$,
      formCan('view')
    ]).pipe(
      // Anonymous map visitors may view published posts without form grants.
      map(([user, canView]) => !user || !!canView)
    );
    this.permissionToEdit$ = formCan('edit_any');
    this.canPublishAny$ = formCan('publish_any');
    this.canPublishOwn$ = formCan('publish_own');
    this.permissionToComment$ = formCan('comment');
    // Moderate (delete) public comments: comment grant or edit_any
    this.canModerateComments$ = combineLatest([
      formCan('comment'),
      formCan('edit_any')
    ]).pipe(map(([c, e]) => !!(c || e)));
    this.editComments$ = this.canModerateComments$;
    // Root report: publish_any, or publish_own when user is the post owner
    this.permissionToPublish$ = combineLatest([
      this.user$,
      this.canPublishAny$,
      this.canPublishOwn$,
      showPayload$
    ]).pipe(
      map(([user, pubAny, pubOwn, post]) => {
        if (pubAny) {
          return true;
        }
        if (!user || !pubOwn) {
          return false;
        }
        return this.isPostOwnedByUser(post || this.post, user);
      })
    );
    this.role$ = this.user$.pipe(map(users => (users ? users.role : '')));
    this.showHistory$ = combineLatest(
      formCan('edit_any'),
      this.viewOnlyMode$,
      (canEdit, loggedIn) => canEdit && loggedIn
    );
    // edit_any OR (owner + edit_own): Individual may edit/update their own reports
    this.showEdit$ = combineLatest([
      this.user$,
      formCan('edit_any'),
      formCan('edit_own'),
      this.viewOnlyMode$,
      showPayload$
    ]).pipe(
      map(([user, editAny, editOwn, loggedIn, post]) => {
        if (!user || !loggedIn) {
          return false;
        }
        if (editAny) {
          return true;
        }
        return !!editOwn && this.isPostOwnedByUser(post || this.post, user);
      })
    );
    this.showDelete$ = combineLatest([
      this.user$,
      formCan('delete'),
      formCan('edit_own'),
      this.viewOnlyMode$,
      showPayload$
    ]).pipe(
      map(([user, canDelete, editOwn, loggedIn, post]) => {
        if (!user || !loggedIn) {
          return false;
        }
        if (canDelete) {
          return true;
        }
        return !!editOwn && this.isPostOwnedByUser(post || this.post, user);
      })
    );
    // Workflow status / levels: General only
    this.isReportStatusEnabled$ = combineLatest([
      this.viewOnlyMode$,
      formCan('edit_any')
    ]).pipe(map(([loggedIn, can]) => !!(loggedIn && can)));
    this.isResponseLevelAllowed$ = combineLatest([
      this.viewOnlyMode$,
      formCan('edit_any')
    ]).pipe(map(([loggedIn, can]) => !!(loggedIn && can)));
    // Individual "Modificar contenido": author edits own content, updates, classification
    this.modifyPersonalReport$ = combineLatest([
      showPayload$,
      this.store.select(selectUser),
      formCan('edit_own')
    ]).pipe(
      map(([post, user, editOwn]) => {
        if (!user || !editOwn) {
          return false;
        }
        return this.isPostOwnedByUser(post || this.post, user);
      })
    );
    // Criticidad / conflicto / análisis: edit_any or (edit_own + owner)
    this.isReportPriorityEnabled$ = combineLatest([
      this.viewOnlyMode$,
      formCan('edit_any'),
      this.modifyPersonalReport$
    ]).pipe(
      map(([loggedIn, editAny, ownMeta]) =>
        !!(loggedIn && (editAny || ownMeta))
      )
    );
  }

  /**
   * True when the current user authored the report (Individual / edit_own).
   * Falls back to allowed_privileges.update when author fields are redacted.
   */
  private isPostOwnedByUser(
    post: Post | null | undefined,
    user: User | null | undefined
  ): boolean {
    if (!post || !user) {
      return false;
    }
    const raw =
      post.user?.id ??
      (post as any).user_id ??
      (typeof (post as any).user === 'number' ? (post as any).user : null);
    if (raw != null && !Number.isNaN(Number(raw))) {
      return Number(raw) === Number(user.id);
    }
    // Owner-only path: API already allows update and author id was stripped
    const privs = (post as any).allowed_privileges as string[] | undefined;
    return Array.isArray(privs) && privs.includes(AllowedPriviledge.Update);
  }

  private inputFormId(): number | null {
    if (this.formId == null || this.formId === '') {
      return null;
    }
    const n = Number(this.formId);
    return !Number.isNaN(n) && n > 0 ? n : null;
  }

  private resolvePostFormId(post: Post | null | undefined): number | null {
    if (!post) {
      return null;
    }
    const fromForm = post.form?.id;
    if (fromForm != null && !Number.isNaN(Number(fromForm))) {
      return Number(fromForm);
    }
    // Relation embedding sometimes exposes only url + id on parent keys
    if (typeof post.form === 'number') {
      return Number(post.form);
    }
    if (typeof (post as any).form === 'object' && (post as any).form?.id != null) {
      return Number((post as any).form.id);
    }
    const raw = (post as any).form_id;
    if (raw != null && !Number.isNaN(Number(raw))) {
      return Number(raw);
    }
    // Bare form id string in some list payloads
    if (typeof (post as any).form === 'string' && /^\d+$/.test((post as any).form)) {
      return Number((post as any).form);
    }
    return null;
  }


  ngOnInit() {
    // Tags resolve category/select labels; load even for anonymous viewers.
    this.catalog.ensureTags();

    this.actions$.pipe(ofType(ECommentsActions.CreateCommentsSuccess)).subscribe(() => {
      if (!FEATURE_POST_COMMENTS || !this.post?.id) {
        return;
      }
      this.store.dispatch(
        new GetCommentsData({ post_id: this.post.id })
      );
    })


    this.actions$.pipe(ofType(ECommentsActions.DeleteCommentSuccess)).subscribe(() => {
      if (!FEATURE_POST_COMMENTS || !this.post?.id) {
        return;
      }
      this.store.dispatch(
        new GetCommentsData({ post_id: this.post.id })
      );
    })

    this.subs.add(
      combineLatest(
        this.actions$.pipe(
          ofType<ShowPostDetail>(ECustomActions.ShowPostDetail)
        ),
        this.store.select(selectTags)
      ).subscribe(([showPostDetailAction, tags]) => {
        if (!showPostDetailAction.payload) {
          this._post = null;
          this.post = null;
          this.analysisDraft = '';
          this.analysisDraftTouched = false;
          this.analysisEditing = false;
          this.analysisComposerActive = false;
          this.resetAnalysisMediaState();
          return;
        }

        this.subs.add(
          this.formService.listAllAttributes().subscribe((formAttributes) => {
            if (showPostDetailAction) {
              // Clone the payload to avoid modifying the read-only properties
              const clonedPayload = { ...showPostDetailAction.payload };
              if (clonedPayload.priority) {
                clonedPayload.priority = PostHelpers.NormalizePostPriority(
                  clonedPayload.priority
                );
              }

              // Modify the values safely in the clonedPayload
              if (clonedPayload.form.id == 1) {
                let id1 = `${environment.pieChart.tag}`;
                clonedPayload.values = { ...clonedPayload.values, [id1]: [] };
              }

              // Keep unsaved analysis text when only media (or other fields) refresh.
              const incomingAnalysis =
                clonedPayload.analysis != null
                  ? String(clonedPayload.analysis)
                  : '';
              const keepAnalysisDraft =
                !!this._post &&
                this._post.id === clonedPayload.id &&
                (this.analysisDraft || '').trim() !==
                  (this._post.analysis != null
                    ? String(this._post.analysis).trim()
                    : '');

              this._post = clonedPayload;
              if (!keepAnalysisDraft) {
                this.analysisDraft = incomingAnalysis;
                this.analysisDraftTouched = false;
                this.analysisEditing = false;
                this.analysisComposerActive = false;
              }
              this.syncAnalysisMediaFromPost(clonedPayload);
              this.post = this.formatPost(
                clonedPayload,
                formAttributes,
                tags
              );
              this.formIdSubject.next(
                this.resolvePostFormId(this.post) ??
                  this.resolvePostFormId(clonedPayload) ??
                  this.inputFormId()
              );

              if (FEATURE_POST_COMMENTS) {
                this.store.dispatch(
                  new GetCommentsData({ post_id: this.post.id })
                );
              }

              if (!this.isEditing && this.post) {
                this.postActivities$ = this.postService.getPostAudit(this.post.id).pipe(switchMap(result => of(result)));
                this.showComments = FEATURE_POST_COMMENTS;
              }
            }
            showPostDetailAction = null;
          })
        );
      })
    );


  }

  ngOnDestroy() {
    this.dismissAnalysisFilePreviewModal();
    this.clearAnalysisFilePreview();
    this.post = null;
    this._post = null;
    if (this.childRef) {
      this.childRef.ngOnDestroy();
    }
    this.subs.unsubscribe();
  }


  private formatPost(
    post: Post,
    formAttributes: FormAttributes,
    tags: Tags
  ): PostEx {
    const formatValues = (
      values: any,
      formAttributes: FormAttributes
    ): FormAttributeEx[] => {
      return Object.keys(values)
        .map(key => ({ key, value: values[key] }))
        .map(({ key, value }) => {
          if (!formAttributes || !formAttributes.results) {
            return null;
          }
          const found = formAttributes.results.find(fa => fa.key === key);
          if (!found || !found.id || found.response_private) {
            return null;
          }
          const fa: FormAttributeEx = {
            ...found,
            key,
            value
          };
          if (
            isRepeatableRowsAttribute({ key, config: found.config }) ||
            (key !== VULNERABILITY_DRIVER_KEY &&
              typeof (Array.isArray(value) ? value[0] : value) === 'string' &&
              String(Array.isArray(value) ? value[0] : value).includes(
                '"population"'
              ))
          ) {
            const catalog = resolveRowsCatalog(found.config);
            fa.vulnerabilityRows = parseVulnerabilityRows(value);
            fa.vulnerabilityDisplay = formatVulnerabilityRowsDisplay(
              value,
              null,
              catalog
            );
            if (fa.vulnerabilityDisplay) {
              fa.value = [fa.vulnerabilityDisplay];
            }
          }
          switch (fa.type) {
            case FormAttributeType.Tags:
              {
                if (tags && tags.results) {
                  fa.value = fa.value.map((v: any) =>
                    tags.results.find((t) => {
                      if (isNaN(v)) {
                        return (t.tag === v);
                      } else {
                        return (t.id === parseInt(v));
                      }
                    })
                  );
                }
              }
              break;

            case FormAttributeType.Int:
            case FormAttributeType.Decimal:
              {
                fa.value = fa.value.map(v => (!isNaN(v)) ? parseFloat(v) : '');
              }
              break;

            case FormAttributeType.DateTime:
              {
                fa.value = fa.value.map(v => {
                  const time = moment
                    .utc(v)
                    .format(environment.format.time);
                  if (time === '00:00:00') {
                    return moment
                      .utc(v)
                      .format(environment.format.date);
                  }
                  return moment
                    .utc(v)
                    .format(environment.format.dateTimeWithoutSec);
                });
              }
              break;

            case FormAttributeType.Media:
              {
                // Solo cargar media si el usuario está logueado
                this.store.select(selectUser).pipe(
                  map(user => !!user)
                ).subscribe(isLoggedIn => {
                  if (isLoggedIn) {
                    fa.value.forEach(
                      v =>
                        this.mediaService
                          .read(parseInt(v))
                          .pipe(map(media => media))
                          .subscribe(media => {
                            let url = media.original_file_url;
                            let type = (media.mime.indexOf(PostMediaType.Video) === -1) ? PostMediaType.Photo : PostMediaType.Video;
                            this.mediaUrls[
                              fa.key
                            ] = { type: type, url: this.sanitizer.bypassSecurityTrustResourceUrl(url) };
                          }),
                      this
                    );
                  }
                });
              }
              break;
          }
          return fa;
        })
        .filter((fa): fa is FormAttributeEx => fa != null);
    };
    const orderValues = (values: FormAttributeEx[]): FormAttributeEx[] => {
      if (values[0] !== null) {
        return values.sort((fa0, fa1) =>
          fa0.priority === fa1.priority ? 0 : fa1.priority < fa0.priority ? 1 : -1
        );
      }
      return [];
    };
    if (post && post.values) {
      return {
        ...post,
        values: orderValues(formatValues(post.values, formAttributes))
      };
    }
    return null;
  }

  /**
   * Patch only the given fields. Never send form `values` from the detail view.
   */
  private updatePost(fields: Partial<Post>) {
    if (!this._post?.id || !fields || !Object.keys(fields).length) {
      return;
    }
    const payload = { id: this._post.id, ...fields } as Post;

    this.subs.add(
      this.postService.update(payload).subscribe({
        next: post => {
          this._post = post;
          this.onPostUpdate.emit();
          this.store.dispatch({
            type: ECustomActions.UpdatedPost,
            payload: post
          });
          this.store.dispatch({
            type: ECustomActions.ShowPostDetail,
            payload: post
          });
        },
        error: () => {
          if (Object.prototype.hasOwnProperty.call(fields, 'published')) {
            this.postService.read(this._post.id).subscribe(post => {
              this._post = post;
              this.store.dispatch({
                type: ECustomActions.ShowPostDetail,
                payload: post
              });
            });
          }
        }
      })
    );
  }

  onChangeStatusClick(status: PostStatus) {
    if (status === this._post.status) return;
    this._post.status = status;
    this.updatePost({ status });
  }

  onChangeStatusDetailClick(change: {
    field: 'status_detail_derivado' | 'status_detail_cerrado';
    value: string;
  }) {
    if (!change?.field) {
      return;
    }
    const next = change.value != null ? String(change.value).trim() : '';
    if (!next) {
      return;
    }
    const prev =
      this._post[change.field] != null
        ? String(this._post[change.field])
        : '';
    if (next === prev) {
      return;
    }
    this._post[change.field] = next;
    this.updatePost({ [change.field]: next } as Partial<Post>);
  }

  onChangeConflictStatusClick(conflictStatus: string) {
    const next = conflictStatus || null;
    const prev = this._post.conflict_status
      ? String(this._post.conflict_status)
      : null;
    if (next === prev) {
      return;
    }
    this._post.conflict_status = next;
    this.updatePost({ conflict_status: next });
  }

  onAnalysisSave() {
    if (!this._post || !this.isAnalysisTextDirty() || this.analysisMediaUploading) {
      if (this.analysisMediaUploading) {
        this.analysisMediaError = 'REPORT_ANALYSIS_FILE_WAIT_UPLOAD';
      }
      return;
    }
    this.analysisDraftTouched = true;
    if (this.isAnalysisTextEmpty() || this.hasInvalidAnalysisText()) {
      return;
    }
    const next = (this.analysisDraft || '').trim();
    this._post.analysis = next;
    this.analysisEditing = false;
    this.analysisComposerActive = false;
    this.analysisDraftTouched = false;
    this.updatePost({ analysis: next });
  }

  onAnalysisCancel() {
    if (!this._post || this.analysisMediaUploading) {
      return;
    }
    this.analysisDraft =
      this._post.analysis != null ? String(this._post.analysis) : '';
    this.analysisDraftTouched = false;
    this.analysisEditing = false;
    this.analysisComposerActive = false;
    this.analysisMediaError = '';
  }

  onAnalysisEdit() {
    this.analysisEditing = true;
    this.analysisComposerActive = true;
    this.analysisDraftTouched = false;
  }

  hasSavedAnalysisText(): boolean {
    const saved =
      this._post?.analysis != null ? String(this._post.analysis).trim() : '';
    return saved.length > 0;
  }

  shouldShowAnalysisEditor(canEditAnalysis: boolean): boolean {
    // Author (Individual) or General may edit analysis on reports they can update.
    if (!canEditAnalysis) {
      return false;
    }
    if (this.analysisEditing || this.analysisComposerActive) {
      return true;
    }
    return !this.hasSavedAnalysisText();
  }

  onAnalysisDraftChange(): void {
    if (!this.hasSavedAnalysisText()) {
      this.analysisComposerActive = true;
    }
  }

  isAnalysisTextDirty(): boolean {
    if (!this._post) {
      return false;
    }
    const next = (this.analysisDraft || '').trim();
    const prev =
      this._post.analysis != null ? String(this._post.analysis).trim() : '';
    return next !== prev;
  }

  hasInvalidAnalysisText(): boolean {
    return hasInvalidIncidentLongText(this.analysisDraft);
  }

  isAnalysisTextEmpty(): boolean {
    return !(this.analysisDraft || '').trim();
  }

  canSaveAnalysisText(): boolean {
    return (
      this.isAnalysisTextDirty() &&
      !this.isAnalysisTextEmpty() &&
      !this.hasInvalidAnalysisText() &&
      !this.analysisMediaUploading
    );
  }

  showAnalysisCharError(): boolean {
    return this.analysisDraftTouched && this.hasInvalidAnalysisText();
  }

  showAnalysisRequiredError(): boolean {
    return this.analysisDraftTouched && this.isAnalysisTextEmpty();
  }

  onAnalysisKeypress(event: KeyboardEvent): void {
    blockIncidentLongTextKeypress(event);
  }

  onAnalysisPaste(event: ClipboardEvent): void {
    blockIncidentLongTextPaste(event);
  }

  onAnalysisBlur(): void {
    this.analysisDraftTouched = true;
  }

  async onAnalysisFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    this.analysisMediaError = '';
    const result = await assertReadableUploadFile(file, {
      kind: 'pdf',
      maxMb: 10
    });
    if (result.ok === false) {
      if (result.errorKey === 'FILE_SIZE_EXCEEDED') {
        this.analysisMediaError = 'REPORT_ANALYSIS_FILE_SIZE_ERROR';
      } else if (result.errorKey === 'FILE_CORRUPT_OR_UNREADABLE') {
        this.analysisMediaError = 'FILE_CORRUPT_OR_UNREADABLE';
      } else {
        // FILE_TYPE_NOT_ALLOWED, FILE_CONTENT_MISMATCH, etc.
        this.analysisMediaError = 'REPORT_ANALYSIS_FILE_TYPE_ERROR';
      }
      input.value = '';
      return;
    }

    this.openAnalysisFileConfirmPreview(file);
    input.value = '';
  }

  openAnalysisFileViewPreview(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this.analysisMediaPreview?.url || this.analysisMediaUploading) {
      return;
    }
    const url = this.analysisMediaPreview.url;
    this.analysisPreviewMode = 'view';
    this.pendingAnalysisFile = null;
    this.revokeAnalysisPdfPreviewUrl();
    this.subs.add(
      this.mediaService.readContentAsBlob(url).subscribe({
        next: blob => {
          this.analysisPdfPreviewBlobUrl = URL.createObjectURL(blob);
          this.analysisPdfPreviewSafe = this.sanitizer.bypassSecurityTrustResourceUrl(
            this.analysisPdfPreviewBlobUrl
          );
          this.openAnalysisFilePreviewModal();
        },
        error: () => {
          // Fallback if blob fetch fails (e.g. cross-origin).
          this.analysisPdfPreviewSafe = this.sanitizer.bypassSecurityTrustResourceUrl(
            url
          );
          this.openAnalysisFilePreviewModal();
        }
      })
    );
  }

  confirmPendingAnalysisFile() {
    const file = this.pendingAnalysisFile;
    this.dismissAnalysisFilePreviewModal();
    this.clearAnalysisFilePreview();
    if (!file) {
      return;
    }
    this.uploadAndPersistAnalysisFile(file);
  }

  cancelPendingAnalysisFile() {
    this.dismissAnalysisFilePreviewModal();
    this.clearAnalysisFilePreview();
  }

  closeAnalysisFileViewPreview() {
    this.dismissAnalysisFilePreviewModal();
    this.clearAnalysisFilePreview();
  }

  downloadAnalysisFile() {
    const url = this.analysisMediaPreview?.url;
    if (!url) {
      return;
    }
    const caption = (this.analysisMediaPreview?.caption || '').trim();
    let fileName =
      getFileNameFromMediaUrl(caption) ||
      getFileNameFromMediaUrl(url, 'analisis.pdf');
    if (!fileName.toLowerCase().endsWith('.pdf')) {
      fileName = `${fileName}.pdf`;
    }
    this.subs.add(
      this.mediaService.readContentAsBlob(url).subscribe({
        next: blob => {
          this.fileSaverService.save(blob, fileName);
        },
        error: () => {
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.rel = 'noopener';
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      })
    );
  }

  onAnalysisFileRemove() {
    if (!this.analysisMediaIdDraft || this.analysisMediaUploading) {
      return;
    }
    this.modalService.open(this.removeAnalysisFileModal, {
      ariaLabelledBy: 'remove-analysis-file-title',
      centered: true,
      backdrop: 'static',
      windowClass: 'analysis-remove-file-modal'
    });
  }

  confirmAnalysisFileRemove(modal) {
    this.analysisMediaIdDraft = null;
    this.analysisMediaPreview = null;
    this.analysisMediaError = '';
    modal.close('confirm');
    this.persistAnalysisMedia(null);
  }

  private openAnalysisFileConfirmPreview(file: File) {
    this.pendingAnalysisFile = file;
    this.analysisPreviewMode = 'confirm';
    this.revokeAnalysisPdfPreviewUrl();
    this.analysisPdfPreviewBlobUrl = URL.createObjectURL(file);
    this.analysisPdfPreviewSafe = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.analysisPdfPreviewBlobUrl
    );
    this.openAnalysisFilePreviewModal();
  }

  private openAnalysisFilePreviewModal() {
    if (!this.analysisFilePreviewModal) {
      this.clearAnalysisFilePreview();
      return;
    }
    if (this.analysisFilePreviewModalRef) {
      this.analysisFilePreviewModalRef.dismiss();
      this.analysisFilePreviewModalRef = null;
    }
    const modalRef = this.modalService.open(
      this.analysisFilePreviewModal,
      this.analysisFilePreviewModalOptions
    );
    this.analysisFilePreviewModalRef = modalRef;
    modalRef.result.then(
      () => {
        if (this.analysisFilePreviewModalRef === modalRef) {
          this.analysisFilePreviewModalRef = null;
        }
        this.clearAnalysisFilePreview();
      },
      () => {
        if (this.analysisFilePreviewModalRef === modalRef) {
          this.analysisFilePreviewModalRef = null;
        }
        this.clearAnalysisFilePreview();
      }
    );
  }

  private dismissAnalysisFilePreviewModal() {
    if (!this.analysisFilePreviewModalRef) {
      return;
    }
    const ref = this.analysisFilePreviewModalRef;
    this.analysisFilePreviewModalRef = null;
    ref.dismiss();
  }

  private clearAnalysisFilePreview() {
    this.pendingAnalysisFile = null;
    this.analysisPreviewMode = 'view';
    this.revokeAnalysisPdfPreviewUrl();
  }

  private revokeAnalysisPdfPreviewUrl() {
    if (this.analysisPdfPreviewBlobUrl) {
      URL.revokeObjectURL(this.analysisPdfPreviewBlobUrl);
      this.analysisPdfPreviewBlobUrl = null;
    }
    this.analysisPdfPreviewSafe = null;
  }

  private uploadAndPersistAnalysisFile(file: File) {
    this.analysisMediaUploading = true;
    this.analysisMediaError = '';
    this.subs.add(
      this.mediaService.attach(file).subscribe({
        next: media => {
          const mediaId = Number(media.id) || null;
          this.analysisMediaIdDraft = mediaId;
          this.analysisMediaPreview = mediaId
            ? {
                url: media.original_file_url || '',
                caption: media.caption || file.name || ''
              }
            : null;
          this.analysisMediaUploading = false;
          if (mediaId) {
            this.persistAnalysisMedia(mediaId);
          }
        },
        error: () => {
          this.analysisMediaUploading = false;
          this.analysisMediaError = 'REPORT_ANALYSIS_FILE_UPLOAD_ERROR';
        }
      })
    );
  }

  private persistAnalysisMedia(mediaId: number | null) {
    if (!this._post) {
      return;
    }
    const prevMediaId = this._post.analysis_media_id
      ? Number(this._post.analysis_media_id)
      : null;
    const nextMediaId = mediaId ? Number(mediaId) : null;
    if (prevMediaId === nextMediaId) {
      return;
    }
    this._post.analysis_media_id = nextMediaId;
    this.updatePost({ analysis_media_id: nextMediaId });
  }

  private resetAnalysisMediaState() {
    this.analysisMediaIdDraft = null;
    this.analysisMediaPreview = null;
    this.analysisMediaUploading = false;
    this.analysisMediaError = '';
  }

  private syncAnalysisMediaFromPost(post: Post) {
    const mediaId = post?.analysis_media_id
      ? Number(post.analysis_media_id)
      : null;
    const previousMediaId = this.analysisMediaIdDraft;
    const previousPreview = this.analysisMediaPreview;

    this.analysisMediaIdDraft = mediaId;
    this.analysisMediaError = '';
    this.analysisMediaUploading = false;

    if (!mediaId) {
      this.analysisMediaPreview = null;
      return;
    }

    if (
      previousMediaId === mediaId &&
      previousPreview?.url
    ) {
      this.analysisMediaPreview = previousPreview;
      return;
    }

    this.subs.add(
      this.mediaService.read(mediaId).subscribe({
        next: media => {
          if (!media?.original_file_url) {
            this.analysisMediaPreview = null;
            return;
          }
          this.analysisMediaPreview = {
            url: media.original_file_url,
            caption: media.caption || ''
          };
        },
        error: () => {
          this.analysisMediaPreview = null;
        }
      })
    );
  }

  onChangeManagementLevelClick(mgmtLev: PostManagementLevel) {
    if (mgmtLev === this._post.mgmt_lev) return;
    this._post.mgmt_lev = mgmtLev;
    this.updatePost({ mgmt_lev: mgmtLev });
  }

  onChangePriorityClick(priority: string) {
    const normalized = PostHelpers.NormalizePostPriority(priority);
    if (
      normalized ===
      PostHelpers.NormalizePostPriority(this._post.priority)
    ) {
      return;
    }
    this._post.priority = normalized;
    this.updatePost({ priority: normalized });
  }

  onChangePublishClick(event) {
    // Independent of edit: form publish grant for this report (own/any).
    this.subs.add(
      this.permissionToPublish$.pipe(take(1)).subscribe(canPublish => {
          if (!canPublish) {
            if (event?.target) {
              event.target.checked = !!this.post?.published;
            }
            return;
          }
          const wantPublished = !!event?.target?.checked;
          if (wantPublished) {
            this._post.published = true;
            this.updatePost({ published: true });
            return;
          }

          // Keep switch on until sustento is confirmed.
          event.target.checked = true;
          this.unpublishReason = '';
          this.unpublishReasonErrorKey = null;
          this.modalService.open(this.unpublishModal, {
            ariaLabelledBy: 'unpublish-modal-title',
            centered: true,
            backdrop: 'static'
          });
        })
    );
  }

  onUnpublishReasonKeypress(event: KeyboardEvent): void {
    blockIncidentLongTextKeypress(event);
  }

  onUnpublishReasonPaste(event: ClipboardEvent): void {
    blockIncidentLongTextPaste(event);
  }

  onUnpublishReasonChange(): void {
    this.unpublishReasonErrorKey = null;
  }

  confirmUnpublish(modal) {
    const reason = (this.unpublishReason || '').trim();
    if (!reason) {
      this.unpublishReasonErrorKey = 'UNPUBLISH_REASON_REQUIRED';
      return;
    }
    if (hasInvalidIncidentLongText(reason)) {
      this.unpublishReasonErrorKey = 'FORM_NO_SPECIAL_CHARACTER';
      return;
    }
    this.unpublishReasonErrorKey = null;
    this._post.published = false;
    this._post.unpublish_reason = reason;
    modal.close('confirm');
    this.updatePost({
      published: false,
      unpublish_reason: reason
    });
  }

  onRefreshClick() {
    this.postService.read(this.post.id).subscribe(post => {
      this.store.dispatch({
        type: ECustomActions.UpdatedPost,
        payload: post
      });
      this.store.dispatch({
        type: ECustomActions.ShowPostDetail,
        payload: post
      });
    });
  }

  onEditClick() {
    const formId =
      this.formIdSubject.value ??
      this.resolvePostFormId(this.post) ??
      this.inputFormId();
    if (formId == null || !this.post?.id) {
      return;
    }

    this.subs.add(
      combineLatest([
        this.store.select(selectUserPermissions),
        this.store.select(selectUser)
      ])
        .pipe(
          take(1),
          map(([up, user]) => {
            if (!up || !user) {
              return false;
            }
            if (AuthHelpers.User.canFormSync(up, formId, 'edit_any')) {
              return true;
            }
            if (!AuthHelpers.User.canFormSync(up, formId, 'edit_own')) {
              return false;
            }
            return this.isPostOwnedByUser(this.post, user);
          })
        )
        .subscribe(canOpen => {
          if (!canOpen) {
            return;
          }
          this.isEditing = true;
          const createIncidentModalRef = this.modalService.open(
            PostEditorPageComponent,
            {
              ariaLabelledBy: 'modal-basic-title',
              windowClass: 'customModalWindowClass post-editor-modal-window',
              size: 'xl'
            }
          );
          createIncidentModalRef.componentInstance.postId = this.post.id.toString();
          createIncidentModalRef.componentInstance.formId = formId;
          createIncidentModalRef.componentInstance.type = this.type;
          createIncidentModalRef.componentInstance.initModalEditor();
          this.hideLocationMap = true;
          this.subs.add(
            createIncidentModalRef.componentInstance.modalCloseEvent.subscribe(
              () => {
                this.hideLocationMap = false;
                if (createIncidentModalRef.componentInstance) {
                  createIncidentModalRef.dismiss();
                }
              }
            )
          );
        })
    );
  }

  onDeleteClick(content) {
    this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title', centered: true });
  }

  onDeleteCommentClick(value) {
    this.store.dispatch(
      new DeleteComment(value)
    );
  }

  onPostClick(value) {
    const payload = {
      post_id: this.post.id,
      message: value.comment
    }
    this.store.dispatch(
      new CreateComments(payload)
    );
  }

  onDeleteConfirmationClick() {
    this.modalService.dismissAll('delete');
    this.postService.delete(this.post).subscribe((result) => {
      this.showDeleteConfirmation = false;
      this.store.dispatch({
        type: ECustomActions.DeletedPost,
        payload: this.post
      });
    }, ($error) => {
      console.log($error);
    });
  }

  onDownloadMediaClick(value: FormAttributeEx) {
    const url$ = this.mediaService
      .read(parseInt(value.value[0]))
      .pipe(
        map(media => media.original_file_url)
      );

    url$.subscribe(url => {
      // console.log('url', url)
      
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      

      // console.log("url limpia", url);
      xhr.onload = () => {
        const blob = xhr.response;
        const fileName = getFileNameFromMediaUrl(url, 'download');

        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = fileName;
        a.style.display = 'none';
        document.body.appendChild(a);

        a.click();
        window.URL.revokeObjectURL(a.href);
        document.body.removeChild(a);
      };

      xhr.open('GET', url);
      xhr.send();
    });
  }

  onShowLinkedPost(parentId: number) {
    this.onShowLinkedPostClick.emit(parentId);
  }

  onLinkToPostClick(flag: boolean) {
    this.onLinkedPostClick.emit({ post: this._post, flag: flag });
  }

  onPostDetailClose() {
    this.onClose.emit();
  }

  getValueByLabel(postValue: any, label: string) {
    let fieldValue;
    fieldValue = (postValue) ? postValue.filter(fa => (fa.label && fa.label.toLocaleLowerCase() === label.toLocaleLowerCase())) : [];
    return (fieldValue && fieldValue[0] && fieldValue[0].value[0] || 0);
  };

  getUser(item, users) {
    const value = JSON.parse(item.value);
    return PostHelpers.FormatUser(value.updated_by, users);
  }

}


